import { useEffect, useMemo, useState } from "react";
import { Button, Card, Input, message, Modal, Radio, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { CloseOutlined, NotificationOutlined, ReloadOutlined, SearchOutlined, SendOutlined } from "@ant-design/icons";
import moment from "moment";
import { api } from "../services/apiClient";

const { Title, Text, Paragraph } = Typography;

function formatDateTime(value) {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
}

export default function SystemFeedbacks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [replying, setReplying] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState("ALL");
  const [notifyUserId, setNotifyUserId] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: `${user.username || user.email || "-"}${user.email ? ` (${user.email})` : ""}`,
      })),
    [users]
  );

  const data = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return items;
    return items.filter((item) => [
      item.username,
      item.email,
      item.message,
      item.adminReply,
      item.status,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(keyword)));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      setItems((await api.get("/api/feedbacks")) || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      setUsers((await api.get("/api/users/options")) || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closeReply = () => {
    setReplying(null);
    setReply("");
  };

  const openReply = (item) => {
    setReplying(item);
    setReply(item.adminReply || "");
  };

  const saveReply = async () => {
    if (!reply.trim()) {
      message.warning("Nhập nội dung trả lời");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/feedbacks/${replying.id}/reply`, { reply: reply.trim() });
      message.success("Đã gửi trả lời");
      closeReply();
      await load();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openNotify = () => {
    setNotifyOpen(true);
    if (users.length === 0) {
      loadUsers();
    }
  };

  const closeNotify = () => {
    setNotifyOpen(false);
    setNotifyTarget("ALL");
    setNotifyUserId(null);
    setNotifyMessage("");
  };

  const sendNotification = async () => {
    if (notifyTarget === "USER" && !notifyUserId) {
      message.warning("Chọn người nhận thông báo");
      return;
    }
    if (!notifyMessage.trim()) {
      message.warning("Nhập nội dung thông báo");
      return;
    }
    setNotifying(true);
    try {
      await api.post("/api/feedbacks/notifications", {
        targetType: notifyTarget,
        userIds: notifyTarget === "USER" ? [notifyUserId] : [],
        message: notifyMessage.trim(),
      });
      message.success("Đã gửi thông báo");
      closeNotify();
      await load();
    } catch (error) {
      message.error(error.message);
    } finally {
      setNotifying(false);
    }
  };

  const columns = [
    {
      title: "USER",
      width: "18%",
      render: (_, item) => (
        <div>
          <Text strong>{item.username || "-"}</Text>
          <br />
          <Text type="secondary">{item.email || "-"}</Text>
        </div>
      ),
    },
    {
      title: "NỘI DUNG",
      width: "34%",
      render: (_, item) => (
        <Paragraph className={item.messageType === "NOTIFICATION" ? "feedback-admin-message feedback-admin-message-notification" : "feedback-admin-message"}>
          {item.message}
        </Paragraph>
      ),
    },
    {
      title: "TRẢ LỜI",
      width: "28%",
      render: (_, item) =>
        item.messageType === "NOTIFICATION" ? (
          <Text strong type="warning">Thông báo chủ động</Text>
        ) : item.adminReply ? (
          <Paragraph className="feedback-admin-message">{item.adminReply}</Paragraph>
        ) : (
          <Text type="secondary">Chưa trả lời</Text>
        ),
    },
    {
      title: "TRẠNG THÁI",
      width: 130,
      dataIndex: "status",
      render: (value, item) => {
        if (item.messageType === "NOTIFICATION") {
          return <Tag color="blue">Đã thông báo</Tag>;
        }
        return <Tag color={value === "ANSWERED" ? "green" : "gold"}>{value === "ANSWERED" ? "Đã trả lời" : "Chờ trả lời"}</Tag>;
      },
    },
    { title: "NGÀY GỬI", width: 150, dataIndex: "createdAt", render: formatDateTime },
    {
      title: "HÀNH ĐỘNG",
      width: 110,
      align: "center",
      render: (_, item) =>
        item.messageType === "NOTIFICATION" ? null : (
          <Tooltip title="Trả lời">
            <Button
              aria-label="Trả lời phản ánh"
              className="feedback-admin-action-button"
              icon={<SendOutlined />}
              onClick={() => openReply(item)}
              type="text"
            />
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Lỗi phản ánh</Title>
          <Text type="secondary">Xem phản ánh lỗi từ người dùng, trả lời hoặc gửi thông báo về widget chat của họ.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm theo user hoặc nội dung phản ánh" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Button icon={<ReloadOutlined />} onClick={load}>Làm mới</Button>
          <Button className="auto-toolbar-primary" icon={<NotificationOutlined />} onClick={openNotify} type="primary">Gửi thông báo</Button>
        </div>
        <Table rowKey="id" loading={loading} columns={columns} dataSource={data} tableLayout="fixed" />
      </Card>
      <Modal
        title="Trả lời phản ánh"
        visible={Boolean(replying)}
        onCancel={closeReply}
        footer={[
          <Button icon={<CloseOutlined />} key="close" onClick={closeReply}>
            Đóng
          </Button>,
          <Button icon={<SendOutlined />} key="send" loading={saving} onClick={saveReply} type="primary">
            Gửi trả lời
          </Button>,
        ]}
        width={760}
      >
        {replying && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div className="feedback-reply-source">
              <Text strong>{replying.username || replying.email}</Text>
              <Paragraph>{replying.message}</Paragraph>
            </div>
            <Input.TextArea rows={5} placeholder="Nhập nội dung trả lời..." value={reply} onChange={(event) => setReply(event.target.value)} />
          </Space>
        )}
      </Modal>
      <Modal
        title="Gửi thông báo"
        visible={notifyOpen}
        onCancel={closeNotify}
        footer={[
          <Button icon={<CloseOutlined />} key="close" onClick={closeNotify}>
            Đóng
          </Button>,
          <Button icon={<SendOutlined />} key="send" loading={notifying} onClick={sendNotification} type="primary">
            Gửi thông báo
          </Button>,
        ]}
        width={680}
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Radio.Group value={notifyTarget} onChange={(event) => setNotifyTarget(event.target.value)}>
            <Radio value="ALL">Gửi tất cả</Radio>
            <Radio value="USER">Gửi cá nhân</Radio>
          </Radio.Group>
          {notifyTarget === "USER" ? (
            <Select
              allowClear
              loading={usersLoading}
              options={userOptions}
              placeholder="Chọn người nhận"
              showSearch
              style={{ width: "100%" }}
              value={notifyUserId}
              onChange={setNotifyUserId}
              optionFilterProp="label"
            />
          ) : null}
          <Input.TextArea rows={5} placeholder="Nhập nội dung thông báo..." value={notifyMessage} onChange={(event) => setNotifyMessage(event.target.value)} />
        </Space>
      </Modal>
    </>
  );
}
